// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useSupportError } from '@/hooks/use-support-error';
import {
  UseMutationOptions,
  UseMutationResult,
  useMutation,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

interface MutationWithGlobalErrorHandlerOptions<
  TData = unknown,
  TError = AxiosError,
  TVariables = void,
  TContext = unknown,
> extends UseMutationOptions<TData, TError, TVariables, TContext> {
  /**
   * Custom error message to show in the global support alert.
   * If not provided, a default message will be used.
   */
  errorMessage?: string;
  /**
   * If true, disables the global error handling for this mutation.
   * Useful when you want to handle all errors locally.
   */
  disableGlobalError?: boolean;
}

/**
 * A wrapper around TanStack Query's useMutation that automatically shows
 * global support errors when mutations fail, unless the error is handled locally.
 */
export function useMutationWithGlobalErrorHandler<
  TData = unknown,
  TError = AxiosError,
  TVariables = void,
  TContext = unknown,
>(
  options: MutationWithGlobalErrorHandlerOptions<
    TData,
    TError,
    TVariables,
    TContext
  >
): UseMutationResult<TData, TError, TVariables, TContext> {
  const { showSupportError, hideSupportError } = useSupportError();
  const {
    errorMessage = 'Something went wrong. Please try again.',
    disableGlobalError = false,
    onError,
    onMutate,
    ...mutationOptions
  } = options;
  return useMutation({
    ...mutationOptions,
    onMutate: (variables) => {
      hideSupportError();
      // Call the original onMutate handler if provided
      if (onMutate) {
        return onMutate(variables);
      }
      return undefined;
    },
    onError: (error, variables, context) => {
      let errorHandled = false;

      // Call the original onError handler if provided
      if (onError) {
        try {
          onError(error, variables, context);
          // If onError completes without throwing, consider it handled
          errorHandled = true;
        } catch (_thrownError) {
          // If onError throws, the error is not handled locally
          errorHandled = false;
        }
      }

      // Show global error if not handled locally and global errors are enabled
      if (!errorHandled && !disableGlobalError) {
        showSupportError(errorMessage);
      }
    },
  });
}
